import React, { useId } from 'react';
import { theme } from '../theme';

// <purpose>THE icon set (picked 2026-10-31, option 3 "action vs fact"). Two drawn languages:
//   - ACTIONS (what a clerk can do) are outlines, ~1.5 px on screen, round ends and joins;
//   - FACTS (what already happened: the retry count, Kopiert, Bearbeitet, Eingang bestätigt, the
//     decision keys, the flag shield) are solid STAMPS with the symbol cut out;
//   - TOGGLES (selection mode, active filters, the Excel lever) turn solid when on.
// Registry: design/design-languages.html, entry `icon_set`. Spec: the picked screen and its drawings
// (`icons-picked.json`, 33 keys); the three fact stamps and the Excel lever's on face were drawn and
// adopted the same day (bearbeitet, Eingang bestätigt, the approved check).</purpose>
// <constraints>
//   - Every drawing lives in a 16-unit box. The stroke is given IN SCREEN PIXELS and converted per
//     size (`strokeFor`), so a 11 px key and an 18 px nav icon draw the same line weight. Each icon's
//     screen weight is the one its picked drawing has at the size it was picked for.
//   - A stamp cuts its symbol with an SVG mask. Mask ids are document-global, so every COPY gets its
//     own id from useId — a list of stamped rows would otherwise all point at the first row's mask,
//     and one row unmounting would blank the others. `icons.test.tsx` pins it.
//   - Decorative only: aria-hidden + focusable="false" on every svg (svgA11y pins it). Meaning lives
//     in the control's name or the pill's word beside the icon.
// </constraints>

export interface IconProps { size?: number; color?: string }

/** Stroke width in box units that draws `screenPx` on screen at `size` px. */
export const strokeFor = (screenPx: number, size: number): number => Math.round((screenPx * 16 / size) * 100) / 100;

const Outline: React.FC<{ size: number; color: string; px?: number; children: React.ReactNode }> = ({ size, color, px = 1.5, children }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16" fill="none"
    stroke={color} strokeWidth={strokeFor(px, size)} strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

/** The cut stroke inside a stamp, in box units (the picked drawings' 1.67). */
export const STAMP_CUT = 1.67;

// A useId value carries characters (":", "«", "»") that are not safe inside url(#…) everywhere.
const safeId = (raw: string): string => 'stx-' + raw.replace(/[^A-Za-z0-9_-]/g, '');

export const Stamp: React.FC<{ size: number; color: string; children: React.ReactNode }> = ({ size, color, children }) => {
  const id = safeId(useId());
  return (
    <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16" fill={color} stroke="none">
      <mask id={id}>
        <rect width="16" height="16" rx="3.5" fill="white" />
        <g fill="none" stroke="black" strokeWidth={STAMP_CUT} strokeLinecap="round" strokeLinejoin="round">{children}</g>
      </mask>
      <g mask={`url(#${id})`}><rect width="16" height="16" rx="3.5" /></g>
    </svg>
  );
};

// ─── Symbols (paths shared by an outline and its stamp) ─────────────────────────────────────────
const RETRY_STAMP = <><path d="M10.8 11.48 A4.3 4.3 0 1 1 10.48 5.4" /><path d="M12.4 3.3 V6.9 H8.8" /></>;
const COPY_STAMP = <><rect x="6.6" y="6.6" width="6.4" height="6.4" rx="0.8" /><path d="M3.2 9.6 V3.2 H9.6" /></>;
const INSTEAD_STAMP = <path d="M4.2 2.8 V7.6 C4.2 9.6 5.2 10.6 7.2 10.6 H12.8 M10 7.8 L12.8 10.6 L10 13.4" />;
const CHECK_STAMP = <path d="M4 8.4 L6.8 11.1 L12 5.2" />;
const PENCIL_STAMP = <path d="M10.2 3.4 L12.6 5.8 L6.6 11.8 L3.6 12.4 L4.2 9.4Z" />;
const RECEIPT_STAMP = <path d="M3.2 4.6 H12.8 V11.4 H3.2Z M3.4 5 L8 8.4 L12.6 5" />;
const BOX = 'M1.5 2.25 H14.5 V5 H1.5Z M2.8 5 V13.75 H13.2 V5';

// ─── Actions (outline) ──────────────────────────────────────────────────────────────────────────
export const CogwheelIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}>
    <path d="M15.17 7.37 L15.17 8.63 L13.34 9.68 L12.97 10.59 L13.52 12.63 L12.63 13.52 L10.59 12.97 L9.68 13.34 L8.63 15.17 L7.37 15.17 L6.32 13.34 L5.41 12.97 L3.37 13.52 L2.48 12.63 L3.03 10.59 L2.66 9.68 L0.83 8.63 L0.83 7.37 L2.66 6.32 L3.03 5.41 L2.48 3.37 L3.37 2.48 L5.41 3.03 L6.32 2.66 L7.37 0.83 L8.63 0.83 L9.68 2.66 L10.59 3.03 L12.63 2.48 L13.52 3.37 L12.97 5.41 L13.34 6.32Z" />
    <circle cx="8" cy="8" r="2.1" />
  </Outline>
);

export const ModeSwapIcon: React.FC<IconProps> = ({ size = 14, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M2 5 H13 M10 1.8 L13.2 5 L10 8.2 M14 11 H3 M6 7.8 L2.8 11 L6 14.2" /></Outline>
);

export const TrashIcon: React.FC<IconProps> = ({ size = 13, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M2 4 H14 M6 4 V2 H10 V4 M3.5 4 L4.3 14 H11.7 L12.5 4" /></Outline>
);

/** Selection mode's lever: four squares; on = two of them solid. */
export const SelectionToggleIcon: React.FC<IconProps & { active?: boolean }> = ({ size = 14, color = 'currentColor', active }) => (
  <Outline size={size} color={color}>
    <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1" fill={active ? color : 'none'} />
    <rect x="9" y="1.5" width="5.5" height="5.5" rx="1" />
    <rect x="1.5" y="9" width="5.5" height="5.5" rx="1" />
    <rect x="9" y="9" width="5.5" height="5.5" rx="1" fill={active ? color : 'none'} />
  </Outline>
);

/** Archive and restore are ONE pair: the same lidded box, the arrow going in (archive) or out. */
export const ArchiveBoxIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d={`${BOX} M8 6.6 V11.6 M5.9 9.5 L8 11.6 L10.1 9.5`} /></Outline>
);

/** The box with the arrow coming out: the Archiv's Auto face and every Archiv row's Wiederherstellen. */
export const RestoreIcon: React.FC<IconProps> = ({ size = 14, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d={`${BOX} M8 11.8 V6.8 M5.9 8.9 L8 6.8 L10.1 8.9`} /></Outline>
);

export const SearchIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}><circle cx="7" cy="7" r="5" /><path d="M10.6 10.6 L14.5 14.5" /></Outline>
);

/** The filter lever: two sliders; on (filters active) = the knobs solid. */
export const FilterSlidersIcon: React.FC<IconProps & { active?: boolean }> = ({ size = 14, color = 'currentColor', active }) => (
  <Outline size={size} color={color}>
    <path d="M1.5 4 H8 M12 4 H14.5 M1.5 12 H4 M8 12 H14.5" />
    <circle cx="10" cy="4" r="2" fill={active ? color : 'none'} />
    <circle cx="6" cy="12" r="2" fill={active ? color : 'none'} />
  </Outline>
);

/** Two arrows; the active direction full, the other at 0.35. */
export const SortDirectionIcon: React.FC<IconProps & { dir?: 'desc' | 'asc' }> = ({ size = 14, color = 'currentColor', dir = 'desc' }) => (
  <Outline size={size} color={color}>
    <g opacity={dir === 'asc' ? 1 : 0.35}><path d="M4.5 14 V2 M1.8 4.8 L4.5 2 L7.2 4.8" /></g>
    <g opacity={dir === 'desc' ? 1 : 0.35}><path d="M11.5 2 V14 M8.8 11.2 L11.5 14 L14.2 11.2" /></g>
  </Outline>
);

export const PopoutIcon: React.FC<IconProps> = ({ size = 15, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M7 2.5 H2.5 V13.5 H13.5 V9 M9.5 2.5 H13.5 V6.5 M13.5 2.5 L7.5 8.5" /></Outline>
);

/** The pane's close: a plain circle on purpose — the bare cross is „Nicht hilfreich" alone. */
export const CircleIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color} px={1.6}><circle cx="8" cy="8" r="5.6" /></Outline>
);

export const CompareIcon: React.FC<IconProps> = ({ size = 14, color = 'currentColor' }) => (
  <Outline size={size} color={color}>
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" />
    <path d="M8 2.5 V13.5 M4 6 H5.5 M10.5 6 H12 M4 9.5 H5.5 M10.5 9.5 H12" />
  </Outline>
);

/** The check. Outline = the Genehmigen verb; `stamp` = the approved fact (decision badge key, the decided row). */
export const CheckIcon: React.FC<IconProps & { stamp?: boolean }> = ({ size = 14, color = 'currentColor', stamp }) => (
  stamp
    ? <Stamp size={size} color={color}>{CHECK_STAMP}</Stamp>
    : <Outline size={size} color={color} px={1.6}><path d="M2.5 8.5 L6.2 12 L13.5 4" /></Outline>
);

export const CrossIcon: React.FC<IconProps> = ({ size = 14, color = 'currentColor' }) => (
  <Outline size={size} color={color} px={1.6}><path d="M3.5 3.5 L12.5 12.5 M12.5 3.5 L3.5 12.5" /></Outline>
);

export const LightningIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M9.6 1.25 L3.4 9.1 H7.7 L6.4 14.75 L12.6 6.9 H8.3 Z" /></Outline>
);

/** The retry ring. Outline = Neu analysieren; `stamp` = the retry COUNT (a fact on the row). */
export const RetryIcon: React.FC<IconProps & { stamp?: boolean }> = ({ size = 14, color = 'currentColor', stamp }) => (
  stamp
    ? <Stamp size={size} color={color}>{RETRY_STAMP}</Stamp>
    : <Outline size={size} color={color}><path d="M11.86 12.15 A5.6 5.6 0 1 1 11.45 4.24" /><path d="M13.6 2 V6.3 H9.3" /></Outline>
);

/** Copy to clipboard (the UUID). `done` = the copied fact: the Kopiert stamp. */
export const CopySmallIcon: React.FC<IconProps & { done?: boolean }> = ({ size = 11, color = 'currentColor', done }) => (
  done
    ? <Stamp size={size} color={color}>{COPY_STAMP}</Stamp>
    : <Outline size={size} color={color}><rect x="5.5" y="5.5" width="9" height="9" rx="1.5" /><path d="M2.5 10.5 V2.5 H10.5" /></Outline>
);

export const UploadMailIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <Outline size={size} color={color} px={2}>
    <path d="M8 4 H1.5 V14 H14.5 V8.5 M1.5 4.5 L8 9 L10 7.6" /><path d="M12.5 6 V1 M10.6 2.9 L12.5 1 L14.4 2.9" />
  </Outline>
);

export const PaperclipIcon: React.FC<IconProps> = ({ size = 13, color = theme.colors.navyMuted }) => (
  <Outline size={size} color={color}><path d="M11 5.5 V10.5 A3 3 0 0 1 5 10.5 V4.5 A1.75 1.75 0 0 1 8.5 4.5 V10" transform="rotate(40 8 8)" /></Outline>
);

/** Bearbeiten. Under 15 px the bare pencil; from 15 px it writes on its line. */
export const PencilIcon: React.FC<IconProps> = ({ size = 15, color = 'currentColor' }) => (
  size >= 15
    ? <Outline size={size} color={color}><path d="M10.5 2.5 L13.5 5.5 L6 13 L2.5 13.5 L3 10Z M2.5 15 H13.5" /></Outline>
    : <Outline size={size} color={color}><path d="M10.5 2 L14 5.5 L6 13.5 L2 14 L2.5 10Z" /></Outline>
);

/** The form-to-Excel lever: a sheet; on = its header row solid. */
export const SpreadsheetRowIcon: React.FC<IconProps & { active?: boolean }> = ({ size = 14, color = 'currentColor', active }) => (
  <Outline size={size} color={color}>
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" />
    {active && <path d="M1.5 2.5 H14.5 V6.2 H1.5Z" fill={color} />}
    <path d="M1.5 6.2 H14.5 M1.5 9.8 H14.5 M6 6.2 V13.5" />
  </Outline>
);

/** Download the picked rows: one arrow onto a line. */
export const DownloadArrowIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M8 1.75 V10.25 M4.6 6.9 L8 10.25 L11.4 6.9 M2.25 13.75 H13.75" /></Outline>
);

/** Download everything in view: three arrows onto the line — bounded vs unbounded, told apart before the click. */
export const MassDownloadIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}>
    <path d="M3.75 1.75 V8.25 M2 6.5 L3.75 8.25 L5.5 6.5 M8 1.75 V9.75 M6.25 8 L8 9.75 L9.75 8 M12.25 1.75 V8.25 M10.5 6.5 L12.25 8.25 L14 6.5 M1.75 13.75 H14.25" />
  </Outline>
);

// ─── Facts (stamps) ─────────────────────────────────────────────────────────────────────────────
/** „Kopiert": the trace key. */
export const CopyIcon: React.FC<IconProps> = ({ size = 11, color = 'currentColor' }) => <Stamp size={size} color={color}>{COPY_STAMP}</Stamp>;

/** The declined-instead decision key: „the clerk did THIS instead". */
export const InsteadIcon: React.FC<IconProps> = ({ size = 11, color = 'currentColor' }) => <Stamp size={size} color={color}>{INSTEAD_STAMP}</Stamp>;

/** „Eingang bestätigt": a receipt went out to the sender. */
export const ReceiptIcon: React.FC<IconProps> = ({ size = 11, color = 'currentColor' }) => <Stamp size={size} color={color}>{RECEIPT_STAMP}</Stamp>;

/** „bearbeitet": the clerk edited the letter (the Bearbeiten ACTION keeps the outline PencilIcon). */
export const PencilStampIcon: React.FC<IconProps> = ({ size = 11, color = 'currentColor' }) => <Stamp size={size} color={color}>{PENCIL_STAMP}</Stamp>;

/** The flag shield beside a flag word: solid, the bar cut out (even-odd, no mask needed). */
export const ShieldIcon: React.FC<IconProps> = ({ size = 12, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16" fill={color} stroke="none">
    <path fillRule="evenodd" d="M8 0.6 L14.6 2.9 V7.6 C14.6 11.6 11.9 14.3 8 15.5 C4.1 14.3 1.4 11.6 1.4 7.6 V2.9Z M4.8 6.8 H11.2 V9 H4.8Z" />
  </svg>
);
